import * as Yup from 'yup';
import { Checkbox } from 'src/components/ui/checkbox';
import FormSectionPage from '../form-section/formSectionPage';
import i18n from 'src/localization/i18n';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import {
    TITLE_REQUEST_PDF_SIZE_ERROR,
    TITLE_REQUEST_SPECIAL_INSTRUCTIONS_ERROR,
} from 'src/localization/resouceKeys';

import { IUser } from 'src/utils/interfaces/user';
import { NUMBER_ONE } from 'src/utils/constants';
import React, { useEffect, useMemo, useState } from 'react';
import './style.scss';

interface ITitleRequestInformation {
    reasonForRequest: string;
    documentRequestType: string;
    titleMaintenanceType: string;
    specialInstructions: string;
    isResubmission: boolean;
    uploadFile: File | null;
}

interface IProps {
    onSave: (values: ITitleRequestInformation) => void;
    accountType?: string;
    isEnabled?: boolean;
    onFormTouched?: () => void;
}

const REASON_FOR_REQUEST_OPTIONS = [
    'Document Request',
    'Title Maintenance',
    'Title Release Status',
];

const DOCUMENT_REQUEST_TYPE_OPTIONS = [
    'State Specific Form',
    'Lien Release',
    'Affidavit of Correction (AOC)',
    'Substitution of Collateral (SOC)',
];

const GENERIC_POA_REQUEST_OPTION = 'Power of Attorney (POA)';

const LEASE_POA_REQUEST_OPTIONS = [
    'Power of Attorney (POA) - Titling and Licensing',
    'Power of Attorney (POA) - Duplicate and Reassignment',
    'Bulk Power of Attorney (POA)',
];

const TITLE_RELEASE_STATUS_OPTIONS = [
    '1-34 days since purchase date',
    '35-60 days since purchase date',
    '61-90 days since purchase date',
    '91+ days since purchase date',
];

const TITLE_MAINTENANCE_TYPE_OPTIONS = [
    'Duplicate Title',
    'State Transfer',
    'Discrepant Title',
    'VIN Change',
    'Transfer of Equity (TOE)',
];

const MAX_SPECIAL_INSTRUCTIONS_LENGTH = 500;
const SPECIAL_INSTRUCTIONS_REGEX = /^[A-Za-z0-9 '.\/&@,:;!?*\-\r\n]*$/;
// eslint-disable-next-line no-magic-numbers
const MAX_UPLOAD_SIZE = 6 * 1024 * 1024;

const TitleRequestInformation: React.FC<IProps> = ({
    onSave,
    accountType = '',
    isEnabled = false,
    onFormTouched,
}) => {
    const [isOpen, setOpen] = useState<boolean>(false);
    const [showIcon, setShowIcon] = useState<boolean | null>(null);
    const [isDisabled, setIsDisabled] = useState<boolean>(false);
    const [uploadError, setUploadError] = useState<string>('');
    const accessUser = useSelector((state: IUser) => {
        return state.accessUser;
    });
    const documentRequestTypeOptions =
        accountType === 'Lease'
            ? [
                  DOCUMENT_REQUEST_TYPE_OPTIONS[0],
                  ...LEASE_POA_REQUEST_OPTIONS,
                  ...DOCUMENT_REQUEST_TYPE_OPTIONS.slice(NUMBER_ONE),
              ]
            : [
                  DOCUMENT_REQUEST_TYPE_OPTIONS[0],
                  GENERIC_POA_REQUEST_OPTION,
                  ...DOCUMENT_REQUEST_TYPE_OPTIONS.slice(NUMBER_ONE),
              ];

    const schema = useMemo(
        () =>
            Yup.object({
                reasonForRequest: Yup.string().required('Field required'),
                documentRequestType: Yup.string().when('reasonForRequest', {
                    is: (value: string) => value !== 'Title Maintenance',
                    then: (schema) => schema.required('Field required'),
                    otherwise: (schema) => schema.optional(),
                }),
                titleMaintenanceType: Yup.string().when('reasonForRequest', {
                    is: 'Title Maintenance',
                    then: (schema) => schema.required('Field required'),
                    otherwise: (schema) => schema.optional(),
                }),
                specialInstructions: Yup.string()
                    .max(MAX_SPECIAL_INSTRUCTIONS_LENGTH, () =>
                        i18n.t(TITLE_REQUEST_SPECIAL_INSTRUCTIONS_ERROR),
                    )
                    .matches(SPECIAL_INSTRUCTIONS_REGEX, () =>
                        i18n.t(TITLE_REQUEST_SPECIAL_INSTRUCTIONS_ERROR),
                    ),
            }),
        [],
    );

    const formik = useFormik<ITitleRequestInformation>({
        initialValues: {
            reasonForRequest: REASON_FOR_REQUEST_OPTIONS[0],
            documentRequestType: DOCUMENT_REQUEST_TYPE_OPTIONS[0],
            titleMaintenanceType: TITLE_MAINTENANCE_TYPE_OPTIONS[0],
            specialInstructions: '',
            isResubmission: false,
            uploadFile: null,
        },
        validationSchema: schema,
        validateOnMount: true,
        onSubmit: (values) => {
            onSave(values);
            setShowIcon(true);
            setOpen(false);
            setIsDisabled(true);
            setUploadError('');
        },
    });

    useEffect(() => {
        if (formik.dirty && !isDisabled && isEnabled) {
            onFormTouched?.();
        }
    }, [formik.dirty, isDisabled, isEnabled, onFormTouched]);

    useEffect(() => {
        const selectedRequestType = formik.values.documentRequestType;
        const shouldResetPoaSelection =
            accountType === 'Lease'
                ? selectedRequestType === GENERIC_POA_REQUEST_OPTION
                : LEASE_POA_REQUEST_OPTIONS.includes(selectedRequestType);

        if (shouldResetPoaSelection) {
            formik.setFieldValue(
                'documentRequestType',
                DOCUMENT_REQUEST_TYPE_OPTIONS[0],
            );
        }
    }, [accountType, formik.values.documentRequestType]);

    const handleEdit = () => {
        setIsDisabled(false);
        setOpen(true);
        setShowIcon(null);
        formik.setTouched({});
        formik.setErrors({});
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.currentTarget.files?.[0] || null;

        if (!selectedFile) {
            formik.setFieldValue('uploadFile', null);
            setUploadError('');
            return;
        }

        const isPdf = selectedFile.type === 'application/pdf';
        if (!isPdf) {
            formik.setFieldValue('uploadFile', null);
            setUploadError(
                'File type not allowed. Just PDF extension is allowed.',
            );
            return;
        }

        if (selectedFile.size > MAX_UPLOAD_SIZE) {
            formik.setFieldValue('uploadFile', null);
            setUploadError(i18n.t(TITLE_REQUEST_PDF_SIZE_ERROR));
            return;
        }

        setUploadError('');
        formik.setFieldValue('uploadFile', selectedFile);
    };

    useEffect(() => {
        if (isEnabled) {
            setOpen(true);
        }
    }, [isEnabled]);

    return (
        <FormSectionPage
            title={'Title Request Information'}
            handlerSubmit={formik.submitForm}
            isOpen={isOpen}
            setOpen={setOpen}
            showIcon={showIcon}
            isDisabled={isDisabled}
            onEdit={handleEdit}
            isFormValid={formik.isValid}
            isBlocked={!isEnabled}
        >
            <div className='title-request-information'>
                <div className='o-fieldset__row'>
                    <p className='c-form-field__label'>Reason for Request*</p>
                    <div className='title-request-information__inline-options'>
                        {REASON_FOR_REQUEST_OPTIONS.map((option) => (
                            <label className='radio-option' key={option}>
                                <input
                                    className='radio-input'
                                    type='radio'
                                    name='reasonForRequest'
                                    value={option}
                                    onChange={(event) => {
                                        const selectedReason =
                                            event.currentTarget.value;

                                        formik.setFieldValue(
                                            'reasonForRequest',
                                            selectedReason,
                                        );

                                        if (
                                            selectedReason ===
                                            'Title Release Status'
                                        ) {
                                            formik.setFieldValue(
                                                'documentRequestType',
                                                TITLE_RELEASE_STATUS_OPTIONS[0],
                                            );
                                        }

                                        if (
                                            selectedReason ===
                                            'Document Request'
                                        ) {
                                            formik.setFieldValue(
                                                'documentRequestType',
                                                DOCUMENT_REQUEST_TYPE_OPTIONS[0],
                                            );
                                        }
                                    }}
                                    checked={
                                        formik.values.reasonForRequest ===
                                        option
                                    }
                                    disabled={isDisabled || !isEnabled}
                                />
                                <span className='radio-label'>{option}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {formik.values.reasonForRequest === 'Document Request' ? (
                    <div className='o-fieldset__row'>
                        <p className='c-form-field__label'>
                            Document Request Type*
                        </p>
                        <div className='title-request-information__stacked-options'>
                            {documentRequestTypeOptions.map((option) => (
                                <div key={option}>
                                    <label className='radio-option'>
                                        <input
                                            className='radio-input'
                                            type='radio'
                                            name='documentRequestType'
                                            value={option}
                                            onChange={(event) => {
                                                formik.setFieldValue(
                                                    'documentRequestType',
                                                    event.currentTarget.value,
                                                );
                                            }}
                                            checked={
                                                formik.values
                                                    .documentRequestType ===
                                                option
                                            }
                                            disabled={isDisabled || !isEnabled}
                                        />
                                        <span className='radio-label'>
                                            {option}
                                        </span>
                                    </label>
                                    {option === GENERIC_POA_REQUEST_OPTION &&
                                    accountType !== 'Lease' &&
                                    formik.values.documentRequestType ===
                                        GENERIC_POA_REQUEST_OPTION ? (
                                        <div className='title-request-information__poa-help'>
                                            <p className='title-request-information__poa-help-text'>
                                                <strong>
                                                    Only duplicate title and
                                                    reassignment POA&apos;s are
                                                    available.
                                                </strong>
                                                <br />
                                                Provide the following info in
                                                Special Instructions section:
                                            </p>
                                            <ul className='title-request-information__poa-help-list'>
                                                <li className='title-request-information__poa-help-bullet'>
                                                    Dealer Code to verify same
                                                    as purchasing dealer
                                                </li>
                                                <li className='title-request-information__poa-help-bullet'>
                                                    Mailing address where form
                                                    should be sent
                                                </li>
                                            </ul>
                                        </div>
                                    ) : null}
                                    {option === 'State Specific Form' &&
                                    formik.values.documentRequestType ===
                                        'State Specific Form' ? (
                                        <div className='title-request-information__poa-help'>
                                            <p className='title-request-information__poa-help-text'>
                                                <strong>
                                                    Required Document(s):&nbsp;
                                                </strong>
                                                Upload any DMV communication
                                                giving reason for state form
                                                request
                                            </p>
                                            <p className='title-request-information__poa-help-text'>
                                                Provide the following info in
                                                Special Instructions section:
                                            </p>
                                            <ul className='title-request-information__poa-help-list'>
                                                <li className='title-request-information__poa-help-bullet'>
                                                    Form State
                                                </li>
                                                <li className='title-request-information__poa-help-bullet'>
                                                    Form Name
                                                </li>
                                                <li className='title-request-information__poa-help-bullet'>
                                                    Form Number
                                                </li>
                                                <li className='title-request-information__poa-help-bullet'>
                                                    Reason for Request
                                                </li>
                                                <li className='title-request-information__poa-help-bullet'>
                                                    Mailing address where form
                                                    should be sent
                                                </li>
                                            </ul>
                                        </div>
                                    ) : null}
                                    {option === 'Lien Release' &&
                                    formik.values.documentRequestType ===
                                        'Lien Release' ? (
                                        <div className='title-request-information__poa-help'>
                                            <p className='title-request-information__poa-help-text'>
                                                <strong>
                                                    Required Document(s):&nbsp;
                                                </strong>
                                                Upload title image/pdf
                                            </p>
                                            <p className='title-request-information__poa-help-text'>
                                                Provide the following info in
                                                Special Instructions section:
                                            </p>
                                            <ul className='title-request-information__poa-help-list'>
                                                <li className='title-request-information__poa-help-bullet'>
                                                    Mailing address where form
                                                    should be sent
                                                </li>
                                            </ul>
                                        </div>
                                    ) : null}
                                    {option ===
                                        'Affidavit of Correction (AOC)' &&
                                    formik.values.documentRequestType ===
                                        'Affidavit of Correction (AOC)' ? (
                                        <div className='title-request-information__poa-help'>
                                            <p className='title-request-information__poa-help-text'>
                                                <strong>
                                                    Required Document(s):&nbsp;
                                                </strong>
                                                Upload updated odometer
                                                statement, if applicable
                                            </p>
                                            <p className='title-request-information__poa-help-text'>
                                                Provide the following info in
                                                Special Instructions section:
                                            </p>
                                            <ul className='title-request-information__poa-help-list'>
                                                <li className='title-request-information__poa-help-bullet'>
                                                    What information is
                                                    incorrect and what
                                                    information is needed for
                                                    correction
                                                </li>
                                            </ul>
                                        </div>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    </div>
                ) : null}

                {formik.values.reasonForRequest === 'Title Release Status' ? (
                    <div className='o-fieldset__row'>
                        <p className='c-form-field__label'>
                            Select Title Release Status*
                        </p>
                        <div className='title-request-information__stacked-options'>
                            {TITLE_RELEASE_STATUS_OPTIONS.map((option) => (
                                <label className='radio-option' key={option}>
                                    <input
                                        className='radio-input'
                                        type='radio'
                                        name='documentRequestType'
                                        value={option}
                                        onChange={(event) => {
                                            formik.setFieldValue(
                                                'documentRequestType',
                                                event.currentTarget.value,
                                            );
                                        }}
                                        checked={
                                            formik.values
                                                .documentRequestType === option
                                        }
                                        disabled={isDisabled || !isEnabled}
                                    />
                                    <span className='radio-label'>
                                        {option}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                ) : null}

                {formik.values.reasonForRequest === 'Title Maintenance' ? (
                    <div className='o-fieldset__row'>
                        <p className='c-form-field__label'>
                            Title Maintenance Type*
                        </p>
                        <div className='title-request-information__stacked-options'>
                            {TITLE_MAINTENANCE_TYPE_OPTIONS.map((option) => (
                                <div key={option}>
                                    <label className='radio-option'>
                                        <input
                                            className='radio-input'
                                            type='radio'
                                            name='titleMaintenanceType'
                                            value={option}
                                            onChange={(event) => {
                                                formik.setFieldValue(
                                                    'titleMaintenanceType',
                                                    event.currentTarget.value,
                                                );
                                            }}
                                            checked={
                                                formik.values
                                                    .titleMaintenanceType ===
                                                option
                                            }
                                            disabled={isDisabled || !isEnabled}
                                        />
                                        <span className='radio-label'>
                                            {option}
                                        </span>
                                    </label>
                                    {option === 'Duplicate Title' &&
                                    formik.values.titleMaintenanceType ===
                                        'Duplicate Title' ? (
                                        <div className='title-request-information__poa-help'>
                                            <p className='title-request-information__poa-help-text'>
                                                <strong>
                                                    Required Document(s):{' '}
                                                </strong>
                                                Upload Odometer Statement
                                            </p>
                                        </div>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    </div>
                ) : null}

                <div className='o-fieldset__row'>
                    <label
                        htmlFor='special-instructions'
                        className='c-form-field__label title-request-information__label'
                    >
                        Special Instructions
                    </label>
                    <div className='title-request-information__special-instructions-box'>
                        <textarea
                            id='special-instructions'
                            className='title-request-information__textarea'
                            name='specialInstructions'
                            value={formik.values.specialInstructions}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            maxLength={MAX_SPECIAL_INSTRUCTIONS_LENGTH}
                            disabled={isDisabled || !isEnabled}
                        />
                        <div className='title-request-information__textarea-bottom-line'></div>
                        {formik.touched.specialInstructions &&
                            formik.errors.specialInstructions && (
                                <p className='c-error-message'>
                                    {formik.errors.specialInstructions}
                                </p>
                            )}
                        <div className='title-request-information__limit-text'>
                            {formik.values.specialInstructions.length}/
                            {MAX_SPECIAL_INSTRUCTIONS_LENGTH} character limit
                        </div>
                        <Checkbox
                            name={
                                accessUser
                                    ? 'Is this an escalated request?'
                                    : 'Is this a resubmission on a previous request?'
                            }
                            value='isResubmission'
                            checked={formik.values.isResubmission}
                            onCheck={(checked) => {
                                formik.setFieldValue('isResubmission', checked);
                            }}
                            isError={false}
                            disabled={isDisabled || !isEnabled}
                        />
                        {formik.values.isResubmission && (
                            <span className='title-request-information__resubmission-note'>
                                &nbsp;(Please wait at least 5 business days from
                                original inquiry to re-submit.)
                            </span>
                        )}
                    </div>
                </div>

                <div className='o-fieldset__row'>
                    <p className='c-form-field__label'>File Upload</p>
                    <div className='c-upload title-request-information__upload-wrapper'>
                        <input
                            className='c-upload__input'
                            type='file'
                            id='title-request-file-upload'
                            aria-describedby='title-request-upload-description'
                            accept='application/pdf'
                            onChange={handleFileUpload}
                            disabled={isDisabled || !isEnabled}
                        />
                        <div className='c-upload__description'>
                            {/* <i
                                className='c-upload__icon'
                                aria-hidden='true'
                            ></i> */}
                            <i className={'c-icon c-icon--[upload]'}></i>
                            <span
                                className='c-upload__description-supplementary-text'
                                id='title-request-upload-description'
                            >
                                {formik.values.uploadFile
                                    ? formik.values.uploadFile.name
                                    : 'Drag & Drop'}
                            </span>
                            <span className='title-request-information__upload-hint'>
                                Allowed file type: PDF. Maximum size: 6MB.
                            </span>
                        </div>
                        <div
                            className='c-upload__button c-btn'
                            aria-hidden='true'
                        >
                            <span>Add File</span>
                        </div>
                    </div>
                    {uploadError ? (
                        <p className='c-error-message title-request-information__upload-error'>
                            {uploadError}
                        </p>
                    ) : null}
                </div>
            </div>
        </FormSectionPage>
    );
};

export default TitleRequestInformation;
